// Client information types
export interface CoAuthor {
  user_id?: string;
  email: string;
}

export interface Client {
  id: string;
  name: string;
  industry: string;
  profile: string;
  relationship: string;
  ERPAccountNumber?: string;
  co_authors?: CoAuthor[];
}

// Customer types
export interface CustomerListItem {
  customer_id: string;
  name: string;
  created_by: string;
  industry_type?: string;
  last_updated: string;
}

export interface CustomerDetailsResponse {
  name: string;
  created_by?: string;
  co_authors?: CoAuthor[];
  industry_type?: string;
  company_profile?: string;
  relationship_summary?: string;
  ERPAccountNumber?: string;
  last_updated?: string;
}

export interface CustomerContactDetailsResponse {
  contact_details: {
    name: string;
    email?: string;
    mobile: string;
    secondary_mobile?: string;
    role: string;
  }[];
}

export interface ArtOfPossibleNote {
  name: string;
  rank: number;
  growth: string;
  priority: string;
}

export interface ArtOfPossibleArea {
  area: string;
  notes: ArtOfPossibleNote[];
}

export interface ArtOfPossibleResponses {
  aops: ArtOfPossibleArea[];
}

export interface CriticalApp {
  entry: string;
  tier: number;
  internal_notes: string | null;
  usage: string;
  name: string;
  location: string;
  customer_facing_notes: string | null;
  app_id?: string; // Added app_id field
}

export interface CriticalAppsResponse {
  critical_apps_list: CriticalApp[];
}

export interface FrameworkDivision {
  division_id?: string;
  division_name: string;
  metrics: string[];
}

export interface FrameworkCategoryData {
  category_id?: string;
  category_name: string;
  divisions: FrameworkDivision[];
}

export interface Framework {
  framework_id?: string;
  focus: string;
  categories: FrameworkCategoryData[];
}

export interface FrameworksResponse {
  customer_id?: string;
  frameworks: Framework[];
}

/** Single item for POST /customer/:customerId/framework/create (all fields required). */
export interface FrameworkItemRequest {
  framework_id: string;
  focus: string;
  category_id: string;
  category_name: string;
  division_id: string;
  division_name: string;
  metrics: string[];
}

export interface IndustryGoal {
  output: string;
  goal: string;
}

export interface IndustryTrendDetail {
  header: string;
  details: string[];
  highlights: string[];
  trend: string;
}

export interface IndustryPriorityDetail {
  caption: string;
  description: string;
  details: string[];
  pillar_alignement: string;
}

export interface IndustryDetailsResponse {
  industry_goals_and_output: IndustryGoal[];
  industry_name: string;
  industry_trend_details: IndustryTrendDetail[];
  industry_priority_details: IndustryPriorityDetail[];
}

export interface MaturityLevel {
  transformation_area: string;
  growth: string;
  notes: string;
  priority: string;
}

export interface MaturityLevelsResponse {
  maturity_levels: MaturityLevel[];
}

// New interface for static maturity transformation areas with coordinates
export interface StaticMaturityTransformationArea {
  transformation_area: string;
  notes: string[];
  xaxis?: number;
  yaxis?: number;
}

export interface StaticMaturityTransformationResponse {
  maturity_transformation_areas: StaticMaturityTransformationArea[];
}

export interface NotesResponse {
  notes: string[];
}

export interface TechnologyDivision {
  tech: string;
  metric: string;
  description: string;
}

export interface KeyTechnology {
  name: string;
  divisions: TechnologyDivision[];
}

export interface StrategicVendorItem {
  vendor: string;
  status: string;
  technology_id?: string;
}

/** Raw group from GET /customer/:id/technology (used to get UUID for create calls). */
export interface TechnologyGroupFromGet {
  group: string;
  technology_group_id: string;
  technologies: Array<{ technology_id: string; name?: string; status?: string; notes?: string }>;
}

export interface TechnologyResponse {
  key_technology: KeyTechnology[];
  strategic_vendors: StrategicVendorItem[];
  /** Raw from GET; use technology_group_id (UUID) for POST create, not group name. */
  technology_groups?: TechnologyGroupFromGet[];
}

/** Request body for POST /customer/:customerId/technology/create (one item per request). */
export interface TechnologyItemRequest {
  technology_group_id: string;
  label: string;
  technology_id: string;
  data: { name: string; status: string; notes?: string };
}

export interface TopProject {
  project_id: string;
  name: string;
  overview_and_goals: string;
  presidio_controlled?: string | null;
  modernization_plan_project?: string | null;
  estimated_completion: string;
  it_initiative_alignment: string;
  start_month?: string;
  end_month?: string;
  dependency_project_id?: string;
  goals?: KeyGoal[];
  presidio_areas?: string[];
}

export interface TopProjectsResponses {
  top_projects: TopProject[];
}


export interface CriticalAppsResponse {
  critical_apps_list: CriticalApp[];
  industry_type: string;
}

export interface TopProjectsResponse {
  top_projects: {
    project_id: string;
    name: string;
    overview_and_goals: string;
    presidio_controlled?: string;
    modernization_plan_project?: string;
    estimated_completion?: string;
    it_initiative_alignment?: string;
    start_month?: string;
    end_month?: string;
    dependency_project_id?: string;
    goals?: KeyGoal[];
    presidio_areas?: string[];
  }[];
  industry_type: string;
}

export interface MaturityLevelsResponse {
  maturity_levels: {
    transformation_area: string;
    priority: string;
    growth: string;
    notes: string;
  }[];
  industry_type: string;
}

export interface ArtOfPossibleResponse {
  aops: {
    area: string;
    description: string;
    technical_impact: string;
    business_impact: string;
    rank: number;
  }[];
  industry_type: string;
}

/** Response shape from GET /customer/:customerId/dashboard */
export interface DashboardApiResponse {
  customer_id: string;
  customer_details: CustomerDetailsResponse;
  goals?: GoalsData;
  projects?: Array<{
    projectId?: string;
    project_id?: string;
    projectName?: string;
    name?: string;
    overview?: string;
    overview_and_goals?: string;
    presidioControlled?: string;
    presidio_controlled?: string;
    modernizationPlanProject?: string;
    modernization_plan_project?: string;
    startDate?: string;
    start_month?: string;
    endDate?: string;
    end_month?: string;
    dependsOnProjectId?: string;
    dependency_project_id?: string;
    goals?: KeyGoal[];
    presidioAreas?: string[];
    presidio_areas?: string[];
  }>;
  aop?: Array<{
    label: string;
    plans?: Array<{
      name?: string;
      technical_impact?: string;
      business_impact?: string;
      rank?: number;
    }>;
  }>;
  framework?: Framework[];
  technologies?: {
    technology_groups?: Array<{
      group?: string;
      technology_group_id?: string;
      technologies?: Array<{ technology_id?: string; name?: string; status?: string; notes?: string }>;
    }>;
    strategic_vendors?: Array<{ vendor?: string; status?: string; technology_id?: string }>;
  };
  applications?: CriticalApp[];
  maturity_transformations?: Record<
    string,
    {
      label: string;
      maturity_levels: Array<{
        step_id: string;
        transformation_area: string;
        growth: string;
        notes: string;
        order: number;
        xaxis: string;
        yaxis: string;
        label_xaxis?: string;
        label_yaxis?: string;
      }>;
    }
  >;
}

export interface CustomerResponse {
  customer_id: string;
  customer_details: CustomerDetailsResponse;
  art_of_possible: ArtOfPossibleResponses;
  critical_apps: CriticalAppsResponse;
  customer_contact_details: CustomerContactDetailsResponse;
  frameworks: FrameworksResponse;
  industry_details: IndustryDetailsResponse;
  maturity_levels: MaturityLevelsResponse;
  notes: NotesResponse;
  technology: TechnologyResponse;
  top_10_projects: TopProjectsResponse;
  goals?: GoalsData;
  maturity_transformations_by_plan?: Record<
    string,
    {
      label: string;
      maturity_levels: Array<{
        step_id: string;
        transformation_area: string;
        growth: string;
        notes: string;
        order: number;
        xaxis: string;
        yaxis: string;
        label_xaxis?: string;
        label_yaxis?: string;
      }>;
    }
  >;
}


// Projects types
export interface Project {
  projectId?: string;
  projectName: string;
  overview: string;
  presidioControlled: string;
  modernizationPlanProject?: string;
  startDate?: string;
  endDate?: string;
  dependsOnProjectId?: string;
  goals?: KeyGoal[];
  presidioAreas?: string[];
}

// Applications types
export interface Application {
  id: string;
  app_id?: string; // Added app_id field
  entry?: string;
  name: string;
  usage: string;
  location: string;
  tier: number;
  customer_facing_notes: string | null;
  internal_notes: string | null;
}

// Art of Possible types
export type PriorityLevel = 'High' | 'Mid' | 'Low' | 'Select';
export type GrowthLevel = 'High' | 'Mid' | 'Low' | 'Select';

export interface TransformationStrategy {
  id: string;
  transformationArea: string;
  notes: string;
  priority: PriorityLevel;
  growth: GrowthLevel;
  rank: number;
  /** Server id for the AOP area; set from GET /customer/:id/aop, required for save */
  aop_id?: string;
  /** Server id for the plan; set from GET /customer/:id/aop, required for save */
  plan_id?: string;
}

// Backend-aligned AOP API types (snake_case for API boundary)
export interface AOPPlanData {
  plan_id: string;
  name: string;
  business_impact?: string;
  technical_impact?: string;
  rank?: number;
}

export interface AOPItem {
  aop_id: string;
  label: string;
  plans: AOPPlanData[];
}

export interface AOPResponse {
  customer_id: string;
  aop_items: AOPItem[];
}

export interface AOPItemCreateRequest {
  aop_id: string;
  label: string;
  plan_id: string;
  data: {
    name: string;
    business_impact: string;
    technical_impact: string;
    rank: number;
  };
}

export interface AOPItemResponse {
  aop_id: string;
  label: string;
  plan_id: string;
  data: {
    name: string;
    business_impact: string;
    technical_impact: string;
    rank: number;
  };
}

// User types
export interface User {
  id: string;
  name: string;
  role: string;
  avatar: string;
  email: string;
}

// Transformation Maturity types
export type TransformationPriority = 'High' | 'Medium' | 'Low';
export type TransformationGrowth = 'select' | 'No Plans' | 'NotStarted' | 'InProduction' | 'InProgress';

export enum MATURITY_TRANSFORMATION_ID {
  NO_MATURITY = "NO_MATURITY",
  MODERN_NETWORKING_ID = "MODERN_NETWORKING_ID",
  DATA_MODERNIZATION_ID = "DATA_MODERNIZATION_ID"
}

export interface TransformationMaturity {
  step_id: string;
  transformationArea: string;
  priority?: string;
  growth: string;
  notes: string;
  xaxis?: number | string;
  yaxis?: number | string;
  label_xaxis?: string | number;
  label_yaxis?: string | number;
  order?: number;
}

// Framework types
export type FrameworkFocus = 'operational' | 'security';

export interface FrameworkCategory {
  id: string;
  name: string;
  focus: FrameworkFocus;
  order: number;
}

export interface FrameworkTool {
  id: string;
  name: string;
  categoryId: string;
  order: number;
}

export interface FrameworkMatrix {
  id: string;
  name: string;
  categoryId: string;
  tools: FrameworkTool[];
}

// Define interfaces for the goals data
export interface KeyGoal {
    id?: string;
    goal: string;
    definition: string;
}

export interface PriorityGoal {
    id?: string;
    name: string;
    pillar_alignment: string;
    details: string[];
}

export interface TrendGoal {
    id?: string;
    TrendHighlights: string;
    IndustryTrend: string;
    TrendDetails: string[];
}

export interface GoalsData {
    key_goals: KeyGoal[];
    priority_goals: PriorityGoal[];
    trend_goals: TrendGoal[];
}

// Roadmap types
export interface RoadmapTask {
  id: string;
  name: string;
  startMonth: number; // Absolute month number (year * 12 + month), e.g., 2026*12 + 12 = 24324 for Dec 2026
  endMonth?: number;   // Absolute month number (year * 12 + month), e.g., 2027*12 + 3 = 24327 for Mar 2027. Optional - if not provided, extends to end of last quarter
  color: string;
  dependencies: string[];
  description?: string;
  status?: 'not-started' | 'in-progress' | 'completed' | 'delayed';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  assignee?: string;
  estimatedHours?: number;
}

export interface RoadmapMilestone {
  id: string;
  quarter: number;
  label: string;
  description?: string;
  type?: 'deliverable' | 'review' | 'decision' | 'launch';
}

export interface RoadmapData {
  tasks: RoadmapTask[];
  milestones?: RoadmapMilestone[]; // Made optional since we're not using milestones for now
  startYear?: number; // Year for the roadmap (defaults to current year)
}

export interface TransformationRoadmapChartProps {
  width?: number;
  height?: number;
  data?: RoadmapData;
  showTooltips?: boolean;
  showLegends?: boolean;
  interactive?: boolean;
  onTaskClick?: (task: RoadmapTask) => void;
  onMilestoneClick?: (milestone: RoadmapMilestone) => void;
}
